import { ORDERSTATUS } from "../../../models/order";

// Order-type buckets the picker/admin flow pills split on. The `?type=` URL param
// carries one of these alongside a flow preset (approve/manage/send).
// Opening and Relocation are merged into one bucket — the backend's negative
// `exclude_order_type` filter lets us fetch "everything that isn't Week" in one
// call.
export type InstoreTypeBucket = "week" | "opening-relocation";

export const INSTORE_TYPE_BUCKETS: InstoreTypeBucket[] = [
  "week",
  "opening-relocation",
];

// Bucket → backend list params. "week" filters positively on the Week type;
// "opening-relocation" uses the negative exclude filter so it covers Opening,
// Relocation (and any future non-Week type) in a single call.
export const INSTORE_TYPE_BUCKET_PARAMS: Record<
  InstoreTypeBucket,
  { order_type_instore_name?: string; exclude_order_type?: string }
> = {
  week: { order_type_instore_name: "Week" },
  "opening-relocation": { exclude_order_type: "Week" },
};

export const INSTORE_TYPE_BUCKET_LABEL: Record<InstoreTypeBucket, string> = {
  week: "Week",
  "opening-relocation": "Opening/Relocation",
};

// Fixed bubble color per type bucket, shared by the flow pills and the instore
// tab badge. Red/orange are avoided (red = promo orders).
export const INSTORE_TYPE_BUCKET_COLOR: Record<InstoreTypeBucket, string> = {
  week: "bg-purple-600 text-white",
  "opening-relocation": "bg-indigo-500 text-white",
};

export function isInstoreTypeBucket(
  value: string | null,
): value is InstoreTypeBucket {
  return value === "week" || value === "opening-relocation";
}

export type InstorePresetKey =
  | "approve"
  | "handle"
  | "manage"
  | "send"
  | "deliver"
  | "archive"
  | "week"
  | "opening-relocation"
  | "all";

export const INSTORE_BASE_PARAMS = {
  limit: 50,
  sort: JSON.stringify({ column: "order_date", order: "desc" }),
};

export const VALID_INSTORE_PRESETS: InstorePresetKey[] = [
  "approve",
  "handle",
  "manage",
  "send",
  "deliver",
  "archive",
  "week",
  "opening-relocation",
  "all",
];

export function getInstoreDefaultPreset(
  _isHq: boolean,
  _isAdmin: boolean,
  _isPicker: boolean,
): InstorePresetKey {
  return "all";
}

export function resolveInstorePresetFilter(key: InstorePresetKey): {
  order_status?: string;
  order_type_instore_name?: string;
  exclude_order_type?: string;
  paramsOverride?: Record<string, any>;
} {
  if (key === "approve") {
    return {
      order_status: ORDERSTATUS.pending,
      paramsOverride: { limit: 10000 },
    };
  }
  if (key === "handle" || key === "manage") {
    return {
      order_status: ORDERSTATUS.ordered,
      paramsOverride: { limit: 10000 },
    };
  }
  if (key === "send") {
    return {
      order_status: ORDERSTATUS.inProgress,
      paramsOverride: { limit: 10000 },
    };
  }
  if (key === "deliver") {
    return {
      order_status: ORDERSTATUS.shipped,
      paramsOverride: { limit: 10000 },
    };
  }
  if (key === "archive") {
    return {
      order_status: ORDERSTATUS.delivered,
      paramsOverride: { limit: 10000 },
    };
  }
  if (key === "week") {
    return { order_type_instore_name: "Week" };
  }
  if (key === "opening-relocation") {
    // Everything that isn't a Week order (Opening + Relocation, plus any future
    // non-Week type). Uses the backend's negative filter instead of a combined
    // "Opening,Relocation" value the API couldn't filter on.
    return { exclude_order_type: "Week" };
  }
  return {};
}
